import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Book, Clock, Award, Check, ShoppingCart, CreditCard, Users, GitMerge } from 'lucide-react';
import toast from 'react-hot-toast';
import ProductCarousel from '../components/Flashcards/ProductCarousel';
import { redirectToStripeCheckout } from '../services/stripeService';
import { getProductStock, initializeFlashcardStock, FLASHCARD_PRODUCT_ID } from '../services/stockService';
import StockIndicator from '../components/Flashcards/StockIndicator';
import PageSEO from '../components/SEO/PageSEO';

const FlashcardsPage = () => {
  const navigate = useNavigate();
  const { dispatch } = useCart();
  const [stockInfo, setStockInfo] = useState({ currentStock: 0, isAvailable: false, isLoading: true });

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Initialiser et récupérer le stock
    const fetchStock = async () => {
      try {
        await initializeFlashcardStock();
        const stock = await getProductStock(FLASHCARD_PRODUCT_ID);
        
        setStockInfo({
          currentStock: stock.currentStock,
          isAvailable: stock.isAvailable,
          isLoading: false
        });
      } catch (error) {
        console.error('Erreur lors de la récupération du stock:', error);
        setStockInfo({
          currentStock: 0,
          isAvailable: false,
          isLoading: false
        });
      }
    };
    
    fetchStock();
  }, []);

  const handleAddToCart = () => {
    if (!stockInfo.isAvailable) {
      toast.error('Ce produit est actuellement en rupture de stock');
      return;
    }
    
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: 'terminale-notions',
        title: 'Flashcards Notions-Définitions SES Terminale',
        price: 15.99,
        quantity: 1
      }
    });
    toast.success('Produit ajouté au panier !');
  };

  const handleBuyNow = async () => {
    if (!stockInfo.isAvailable) {
      toast.error('Ce produit est actuellement en rupture de stock');
      return;
    }
    
    try {
      await redirectToStripeCheckout();
    } catch (error) {
      toast.error('Erreur lors de la redirection vers la page de paiement');
    }
  };

  // Schéma JSON-LD pour le produit
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Flashcards Notions-Définitions SES Terminale",
    "image": "https://cours-ses.fr/Logo SES.png",
    "description": "110 cartes couvrant l'intégralité du programme de Terminale, organisées par chapitre avec un code couleur distinct pour faciliter vos révisions.",
    "brand": {
      "@type": "Brand",
      "name": "Comprendre les SES"
    },
    "offers": {
      "@type": "Offer",
      "price": "15.99",
      "priceCurrency": "EUR",
      "availability": stockInfo.isAvailable ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "url": "https://cours-ses.fr/flashcards",
      "priceValidUntil": "2025-12-31",
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": "FR",
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "3.99",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "FR"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": "1",
            "maxValue": "2",
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": "1",
            "maxValue": "3",
            "unitCode": "DAY"
          }
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageSEO
        title="Flashcards SES Terminale | Mémorisation Efficace des Notions"
        description="Flashcards SES pour réviser efficacement le programme de Terminale. 110 cartes avec définitions et concepts clés, organisées par chapitre avec code couleur."
        path="/flashcards"
        image="https://cours-ses.fr/Logo SES.png"
        type="product"
        schema={schema}
      />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#9a6ebd]/10 to-white pt-32 pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-12 lg:items-start">
              {/* Colonne droite (première sur mobile) */}
              <div className="order-1 lg:order-2">
                <div className="lg:sticky lg:top-32">
                  {/* Carousel */}
                  <ProductCarousel />

                  {/* Badges sous le carousel - version desktop uniquement */}
                  <div className="hidden lg:flex lg:flex-col lg:gap-4 lg:mt-12">
                    <div className="bg-white p-4 rounded-xl shadow-lg border border-[#ceace8] flex items-center">
                      <div className="w-12 h-12 bg-[#9a6ebd] rounded-full flex items-center justify-center mr-4">
                        <Check className="text-white" size={24} />
                      </div>
                      <div>
                        <p className="font-semibold text-[#323c5f]">Livraison rapide</p>
                        <p className="text-gray-600">Seulement 3,99€ en France métropolitaine</p>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-lg border border-[#ceace8] flex items-center">
                      <div className="w-12 h-12 bg-[#9a6ebd] rounded-full flex items-center justify-center mr-4">
                        <Award className="text-white" size={24} />
                      </div>
                      <div>
                        <p className="font-semibold text-[#323c5f]">Qualité garantie</p>
                        <p className="text-gray-600">Validé par des professeurs de SES</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Colonne gauche (deuxième sur mobile) */}
              <div className="order-2 lg:order-1 lg:sticky lg:top-32">
                {/* Badges version mobile uniquement */}
                <div className="flex justify-between mb-6 lg:hidden">
                  <div className="flex items-center text-sm text-gray-600">
                    <Check className="text-[#9a6ebd] mr-1" size={16} />
                    <span>Livraison 3,99€</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Award className="text-[#9a6ebd] mr-1" size={16} />
                    <span>Validé par des profs</span>
                  </div>
                </div>

                {/* Titre */}
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#323c5f] mb-6">
                  Flashcards Notions-Définitions SES Terminale
                </h1>

                {/* Prix et indicateur de stock */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                  <div className="text-3xl font-bold text-[#9a6ebd]">
                    15,99€
                  </div>
                  
                  {stockInfo.isLoading ? (
                    <div className="animate-pulse bg-gray-200 h-10 w-32 rounded-lg"></div>
                  ) : (
                    <StockIndicator 
                      currentStock={stockInfo.currentStock} 
                      isAvailable={stockInfo.isAvailable} 
                    />
                  )}
                </div>

                {/* Description */}
                <div className="bg-white rounded-xl p-6 mb-8 shadow-lg border border-[#ceace8]">
                  <p className="text-gray-600 mb-4">
                    Salut à toi futur économiste ! 👋 On est super contents de te présenter notre boîte de flashcards 
                    Notions-Définitions SES Terminale, un projet qui nous tient vraiment à cœur. En tant que grands passionnés, 
                    on a mis tout notre savoir-faire et notre expérience pour créer l'outil parfait qui va 
                    t'accompagner tout au long de ton année de Terminale.
                  </p>
                  <p className="text-gray-600 mb-4">
                    On sait à quel point il peut être difficile de mémoriser toutes les notions du programme 
                    de SES. C'est pour ça qu'on a conçu ces cartes avec amour : chaque définition, chaque 
                    concept a été soigneusement sélectionné et expliqué de la manière la plus claire possible. 🎯
                  </p>
                  <p className="text-gray-600">
                    Le petit plus ? On a ajouté un code couleur par chapitre pour que tu puisses facilement 
                    t'y retrouver, et on a fait plastifier les cartes pour qu'elles résistent à toutes tes 
                    sessions de révision intensives ! 🚀
                  </p>
                </div>

                {/* Boutons */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  {stockInfo.isAvailable ? (
                    <>
                      <button
                        onClick={handleBuyNow}
                        className="w-full bg-[#9a6ebd] text-white px-6 py-4 rounded-xl hover:bg-[#8a5dad] transition-colors flex items-center justify-center text-lg font-semibold"
                      >
                        <CreditCard className="mr-2" size={20} />
                        {"Acheter à 15,99€"}
                      </button>
                      <button
                        onClick={handleAddToCart}
                        className="w-full bg-white text-[#9a6ebd] border-2 border-[#9a6ebd] px-6 py-4 rounded-xl hover:bg-[#9a6ebd]/5 transition-colors flex items-center justify-center text-lg font-semibold"
                      >
                        <ShoppingCart className="mr-2" size={20} />
                        Ajouter au panier
                      </button>
                    </>
                  ) : (
                    <button
                      disabled
                      className="w-full bg-gray-300 text-gray-600 px-6 py-4 rounded-xl cursor-not-allowed flex items-center justify-center text-lg font-semibold"
                    >
                      Rupture de stock
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className="border-b border-[#9a6ebd]/10" />

        {/* Features Section */}
        <section className="py-16 bg-[#9a6ebd]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <Book className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#323c5f] mb-2">
                  Programme complet
                </h3>
                <p className="text-gray-600">
                  110 cartes couvrant l'intégralité du programme de Terminale, organisées par chapitre.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <Clock className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#323c5f] mb-2">
                  Révisions efficaces
                </h3>
                <p className="text-gray-600">
                  Format pratique et transportable pour réviser n'importe où, n'importe quand.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <Award className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#323c5f] mb-2">
                  Qualité premium
                </h3>
                <p className="text-gray-600">
                  Cartes plastifiées résistantes avec code couleur par chapitre pour une meilleure organisation.
                </p>
              </div>
            </div>
          </div>
        </section>
        <div className="border-b border-[#9a6ebd]/10" />

        {/* Content Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#323c5f] mb-4">
                  Contenu des flashcards
                </h2>
                <p className="text-xl text-gray-600">
                  Chaque carte est soigneusement conçue pour faciliter la mémorisation des concepts essentiels.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-[#ceace8]">
                {/* Économie */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center text-[#9a6ebd] mb-4">
                    <Book className="mr-2" size={24} />
                    <h3 className="text-xl font-semibold">Économie</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#323c5f]">Croissance économique</span>
                      <span className="px-3 py-1 bg-[#9a6ebd]/10 rounded-full text-sm font-medium text-[#9a6ebd]">
                        12 cartes
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#323c5f]">Commerce international</span>
                      <span className="px-3 py-1 bg-[#9a6ebd]/10 rounded-full text-sm font-medium text-[#9a6ebd]">
                        10 cartes
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#323c5f]">Chômage</span>
                      <span className="px-3 py-1 bg-[#9a6ebd]/10 rounded-full text-sm font-medium text-[#9a6ebd]">
                        15 cartes
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#323c5f]">Europe</span>
                      <span className="px-3 py-1 bg-[#9a6ebd]/10 rounded-full text-sm font-medium text-[#9a6ebd]">
                        8 cartes
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sociologie */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center text-[#9a6ebd] mb-4">
                    <Users className="mr-2" size={24} />
                    <h3 className="text-xl font-semibold">Sociologie</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#323c5f]">Structure sociale</span>
                      <span className="px-3 py-1 bg-[#9a6ebd]/10 rounded-full text-sm font-medium text-[#9a6ebd]">
                        13 cartes
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#323c5f]">Mobilité sociale</span>
                      <span className="px-3 py-1 bg-[#9a6ebd]/10 rounded-full text-sm font-medium text-[#9a6ebd]">
                        8 cartes
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#323c5f]">Travail-Emploi</span>
                      <span className="px-3 py-1 bg-[#9a6ebd]/10 rounded-full text-sm font-medium text-[#9a6ebd]">
                        14 cartes
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#323c5f]">Engagement politique</span>
                      <span className="px-3 py-1 bg-[#9a6ebd]/10 rounded-full text-sm font-medium text-[#9a6ebd]">
                        16 cartes
                      </span>
                    </div>
                  </div>
                </div>

                {/* Regards Croisés */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center text-[#9a6ebd] mb-4">
                    <GitMerge className="mr-2" size={24} />
                    <h3 className="text-xl font-semibold">Regards Croisés</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#323c5f]">Environnement</span>
                      <span className="px-3 py-1 bg-[#9a6ebd]/10 rounded-full text-sm font-medium text-[#9a6ebd]">
                        14 cartes
                      </span>
                    </div>
                  </div>
                </div>

                {/* Total */}
                <div className="p-6 bg-[#9a6ebd]/5">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-[#323c5f]">Total</span>
                    <span className="px-3 py-1 bg-[#9a6ebd] text-white rounded-full text-sm font-medium">
                      110 cartes
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className="border-b border-[#9a6ebd]/10" />

        {/* CTA Section */}
        <section className="py-16 bg-[#9a6ebd]/20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-[#323c5f] mb-6">
              Prêt à booster vos révisions ?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Commandez vos flashcards maintenant !
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {stockInfo.isAvailable ? (
                <>
                  <button
                    onClick={handleBuyNow}
                    className="bg-[#9a6ebd] text-white px-8 py-4 rounded-xl hover:bg-[#8a5dad] transition-colors flex items-center justify-center text-lg font-semibold"
                  >
                    <CreditCard className="mr-2" size={20} />
                    {"Acheter à 15,99€"}
                  </button>
                  <button
                    onClick={handleAddToCart}
                    className="bg-white text-[#9a6ebd] border-2 border-[#9a6ebd] px-8 py-4 rounded-xl hover:bg-[#9a6ebd]/5 transition-colors flex items-center justify-center text-lg font-semibold"
                  >
                    <ShoppingCart className="mr-2" size={20} />
                    Ajouter au panier
                  </button>
                </>
              ) : (
                <button
                  disabled
                  className="bg-gray-300 text-gray-600 px-8 py-4 rounded-xl cursor-not-allowed flex items-center justify-center text-lg font-semibold"
                >
                  Rupture de stock
                </button>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FlashcardsPage;